import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FiRefreshCw } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import { useTicketStatuses } from "../../hooks/useTicketStatuses";
import { hasScreenAccess } from "../../utils/permissions";
import ConfirmDialog from "./listing/ConfirmDialog";
import ListingEmptyState from "./listing/ListingEmptyState";
import StatusBadge from "./listing/StatusBadge";
import TableSkeleton from "./listing/TableSkeleton";
import { LISTING_CARD_CLASS, LISTING_PRIMARY_BUTTON_CLASS } from "./listing/listingStyles";

const STATUS_KEY_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";
const CHECKBOX_CLASS = "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

const slugifyStatusKey = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

const emptyForm = {
  statusKey: "",
  name: "",
  colorCode: "#3B82F6",
  isFinalStatus: false,
  active: true,
};

const TicketStatusSettingsPanel = () => {
  const t = useCallCenterT();
  const statusesApi = useTicketStatuses();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canManageStatuses = hasScreenAccess("CC_SETTINGS", "canCreate");
  const isEditing = Boolean(editing);

  const schema = useMemo(
    () =>
      Yup.object({
        statusKey: Yup.string()
          .trim()
          .matches(STATUS_KEY_PATTERN, t("ccStatusKeyInvalid"))
          .required(t("ccStatusKeyRequired")),
        name: Yup.string().trim().required(t("ccNameRequired")),
        colorCode: Yup.string().matches(/^#([0-9a-f]{3}){1,2}$/i, t("ccInvalidHexColor")).nullable(),
        isFinalStatus: Yup.boolean(),
        active: Yup.boolean(),
      }),
    [t]
  );

  const formInitialValues = useMemo(
    () =>
      editing
        ? {
            statusKey: editing.statusKey || "",
            name: editing.name || "",
            colorCode: editing.colorCode || "#3B82F6",
            isFinalStatus: Boolean(editing.isFinalStatus),
            active: editing.active !== false,
          }
        : emptyForm,
    [editing]
  );

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const data = await statusesApi.list({ includeInactive: true });
      setStatuses(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t("ccToastLoadStatusesFailed"));
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (values, { resetForm }) => {
    try {
      if (isEditing) {
        await statusesApi.update({
          id: editing.id,
          name: values.name.trim(),
          colorCode: values.colorCode || undefined,
          isFinalStatus: Boolean(values.isFinalStatus),
          active: Boolean(values.active),
        });
        toast.success(t("ccToastStatusUpdated"));
        setEditing(null);
      } else {
        await statusesApi.create({
          statusKey: values.statusKey.trim().toUpperCase(),
          name: values.name.trim(),
          colorCode: values.colorCode || undefined,
          isFinalStatus: Boolean(values.isFinalStatus),
          active: Boolean(values.active),
        });
        toast.success(t("ccToastStatusSaved"));
      }
      resetForm({ values: emptyForm });
      loadStatuses();
    } catch (error) {
      toast.error(error.message || (isEditing ? t("ccToastStatusUpdateFailed") : t("ccToastStatusSaveFailed")));
    }
  };

  const handleToggleActive = async (status) => {
    try {
      setBusyId(status.id);
      await statusesApi.update({ id: status.id, active: !status.active });
      toast.success(t("ccToastStatusUpdated"));
      if (editing?.id === status.id) {
        setEditing(null);
      }
      loadStatuses();
    } catch (error) {
      toast.error(error.message || t("ccToastStatusUpdateFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setBusyId(deleteTarget.id);
      await statusesApi.remove(deleteTarget.id);
      toast.success(t("ccToastStatusDeleted"));
      if (editing?.id === deleteTarget.id) {
        setEditing(null);
      }
      setDeleteTarget(null);
      loadStatuses();
    } catch (error) {
      toast.error(error.message || t("ccToastStatusDeleteFailed"));
    } finally {
      setBusyId(null);
    }
  };

  if (!canManageStatuses) {
    return (
      <section className={LISTING_CARD_CLASS}>
        <div className="border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">{t("ccWorkflowSettingsTitle")}</h3>
          <p className="mt-1 text-sm text-slate-500">{t("ccStatusSettingsViewOnly")}</p>
        </div>
        {loading ? (
          <div className="p-4">
            <TableSkeleton columns={2} rows={4} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 p-6 md:grid-cols-2">
            {statuses.map((status) => (
              <div key={`${status.statusKey}-${status.id}`} className="rounded-xl border border-slate-200 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {status.statusKey}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: status.colorCode || "#94a3b8" }}
                  />
                  <span className="text-sm font-semibold text-slate-800">{status.name}</span>
                  <StatusBadge
                    status={status.active ? "ACTIVE" : "CANCELLED"}
                    label={status.active ? t("ccActive") : t("ccInactive")}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <section className={LISTING_CARD_CLASS}>
            <div className="flex items-center gap-2.5 border-b border-slate-100 px-6 py-4">
              <h3 className="font-semibold text-slate-900">{isEditing ? t("ccEditStatus") : t("ccAddNewStatus")}</h3>
              <span className="rounded-md bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#236DB4]">
                {t("ccConfiguration")}
              </span>
            </div>

            <Formik enableReinitialize initialValues={formInitialValues} validationSchema={schema} onSubmit={handleSave}>
              {({ values, errors, touched, setFieldValue, resetForm }) => (
                <Form className="space-y-5 p-6">
                  <div>
                    <label className={LABEL_CLASS}>{t("ccDisplayName")}</label>
                    <Field
                      name="name"
                      placeholder={t("ccDisplayNamePlaceholder")}
                      className={FIELD_CLASS}
                      onBlur={(event) => {
                        if (!isEditing && !values.statusKey.trim()) {
                          const suggested = slugifyStatusKey(event.target.value);
                          if (suggested) {
                            setFieldValue("statusKey", suggested);
                          }
                        }
                      }}
                    />
                    {errors.name && touched.name ? (
                      <div className="mt-1 text-xs text-red-500">{errors.name}</div>
                    ) : null}
                  </div>

                  <div>
                    <label className={LABEL_CLASS}>{t("ccStatusKeyLabel")}</label>
                    <Field
                      name="statusKey"
                      disabled={isEditing}
                      placeholder={t("ccStatusKeyPlaceholder")}
                      className={`${FIELD_CLASS} font-mono uppercase ${isEditing ? "cursor-not-allowed bg-slate-50 text-slate-400" : ""}`}
                      onChange={(event) => setFieldValue("statusKey", slugifyStatusKey(event.target.value))}
                    />
                    <p className="mt-1.5 text-[11px] text-slate-400">
                      {isEditing ? t("ccStatusKeyLockedHint") : t("ccStatusKeyHint")}
                    </p>
                    {errors.statusKey && touched.statusKey ? (
                      <div className="mt-1 text-xs text-red-500">{errors.statusKey}</div>
                    ) : null}
                  </div>

                  <div>
                    <label className={LABEL_CLASS}>{t("ccColorTheme")}</label>
                    <div className="flex h-11 overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-[#2B7FFF] focus-within:ring-2 focus-within:ring-[#2B7FFF]/15">
                      <Field
                        name="colorCode"
                        type="color"
                        className="h-11 w-11 shrink-0 cursor-pointer border-0 bg-transparent p-1.5"
                      />
                      <Field
                        name="colorCode"
                        className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 font-mono text-sm uppercase text-slate-700 outline-none"
                      />
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#0f172a] p-4">
                    <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {t("ccLiveUiPreview")}
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                        style={{ backgroundColor: values.colorCode || "#3B82F6" }}
                      >
                        {values.name || t("ccPreviewStatus")}
                      </span>
                      <span className="text-[12px] text-slate-400">{values.statusKey || t("ccStatusKeyLabel")}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="flex cursor-pointer items-start gap-3">
                      <Field type="checkbox" name="isFinalStatus" className={`${CHECKBOX_CLASS} mt-0.5`} />
                      <div>
                        <span className="block text-sm font-semibold text-slate-800">{t("ccFinalStatus")}</span>
                        <span className="block text-[12px] text-slate-400">{t("ccFinalStatusHint")}</span>
                      </div>
                    </label>
                    <label className="flex cursor-pointer items-center gap-3">
                      <Field type="checkbox" name="active" className={CHECKBOX_CLASS} />
                      <span className="text-sm font-semibold text-slate-800">{t("ccEnabled")}</span>
                    </label>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button type="submit" className={`flex-1 ${LISTING_PRIMARY_BUTTON_CLASS} justify-center`}>
                      {isEditing ? t("ccUpdateWorkflowStatus") : t("ccSaveWorkflowStatus")}
                    </button>
                    {isEditing ? (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(null);
                          resetForm({ values: emptyForm });
                        }}
                        className="h-10 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {t("ccCancelEdit")}
                      </button>
                    ) : null}
                  </div>
                </Form>
              )}
            </Formik>
          </section>
        </div>

        <div className="lg:col-span-7">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              {t("ccStatusLibrary")}
            </h3>
            <button
              type="button"
              onClick={loadStatuses}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2B7FFF] hover:text-[#1d4ed8] disabled:opacity-50"
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              {t("ccSyncStatuses")}
            </button>
          </div>

          {loading ? (
            <div className={`${LISTING_CARD_CLASS} p-4`}>
              <TableSkeleton columns={2} rows={5} />
            </div>
          ) : !statuses.length ? (
            <ListingEmptyState icon={FiRefreshCw} title={t("ccNoCustomStatuses")} hint={t("ccWorkflowSettingsSubtitle")} />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {statuses.map((status) => (
                <div
                  key={`${status.statusKey}-${status.id}`}
                  className={`relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white py-4 pl-5 pr-4 shadow-sm transition ${
                    status.active ? "" : "opacity-70"
                  } ${editing?.id === status.id ? "border-[#2B7FFF] ring-2 ring-[#2B7FFF]/30" : ""}`}
                >
                  <span
                    className="absolute inset-y-0 left-0 w-1.5"
                    style={{ backgroundColor: status.colorCode || "#94a3b8" }}
                    aria-hidden="true"
                  />
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {status.statusKey}
                    </span>
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {status.builtIn ? (
                        <span className="rounded-full bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#236DB4]">
                          {t("ccBuiltInStatus")}
                        </span>
                      ) : null}
                      {status.isFinalStatus ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                          {t("ccFinal")}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="truncate text-sm font-semibold text-slate-900">{status.name}</div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setEditing(status)}
                        className="text-sm font-medium text-[#2B7FFF] hover:underline"
                      >
                        {t("ccEdit")}
                      </button>
                      <button
                        type="button"
                        disabled={busyId === status.id}
                        onClick={() => handleToggleActive(status)}
                        className="text-sm font-medium text-amber-600 hover:underline disabled:opacity-50"
                      >
                        {status.active ? t("ccDeactivate") : t("ccActivate")}
                      </button>
                      {status.deletable ? (
                        <button
                          type="button"
                          disabled={busyId === status.id}
                          onClick={() => setDeleteTarget(status)}
                          className="text-sm font-medium text-rose-600 hover:underline disabled:opacity-50"
                        >
                          {t("ccDelete")}
                        </button>
                      ) : null}
                    </div>
                    <StatusBadge
                      status={status.active ? "ACTIVE" : "CANCELLED"}
                      label={status.active ? t("ccActive") : t("ccInactive")}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 rounded-2xl bg-gradient-to-b from-[#1D4ED8] to-[#1E3A8A] p-6 text-white">
            <h4 className="mb-1 font-semibold">{t("ccStatusProTipTitle")}</h4>
            <p className="text-sm leading-relaxed text-white/90">{t("ccStatusProTipBody")}</p>
            <Link
              to="/call-center/tickets"
              className="mt-3 inline-block text-sm font-medium text-white underline underline-offset-2"
            >
              {t("ccStatusManageFromTickets")}
            </Link>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t("ccConfirmDialogTitle")}
        message={t("ccConfirmDeleteStatus")}
        cancelLabel={t("ccCancel")}
        confirmLabel={t("ccConfirmDialogProceed")}
        processingLabel={t("ccProcessing")}
        variant="danger"
        loading={Boolean(deleteTarget && busyId === deleteTarget.id)}
        onConfirm={confirmDelete}
        onClose={() => !busyId && setDeleteTarget(null)}
      />
    </>
  );
};

export default TicketStatusSettingsPanel;
